import { useState, useEffect, type CSSProperties, type ReactNode } from 'react';
import {
  useCpcFactorSets,
  useCpcFactorSetDetail,
  useCreateFactorSet,
  useUpdateFactorSet,
  useDeleteFactorSet,
  useActivateFactorSet,
} from './cpc-hooks';
import { RateAdminTab } from './RateAdminTab';
import { ClockodoAdminTab } from './ClockodoAdminTab';
import { AuditLogTab } from './AuditLogTab';
import type {
  FactorSet,
  SizeClass,
  Framework,
  PhasePT,
  CpcRoleDefinition,
} from './cpc-types';

// ── Konstanten ────────────────────────────────────────────────────────────────

const SIZE_CLASSES: SizeClass[] = ['S', 'M', 'L'];

const FRAMEWORKS: Framework[] = [
  'iso27001', 'marisk', 'dora', 'ebaOutsourcing', 'nis2', 'cra', 'iso22301',
];

const FRAMEWORK_LABELS: Record<Framework, string> = {
  iso27001: 'ISO 27001',
  marisk: 'MaRisk',
  dora: 'DORA',
  ebaOutsourcing: 'EBA Outsourcing',
  nis2: 'NIS2',
  cra: 'CRA',
  iso22301: 'ISO 22301',
};

const SECTORS = [
  { key: 'financial', label: 'Finanzinstitute' },
  { key: 'financial_service_provider', label: 'Finanzdienstleister' },
  { key: 'healthcare', label: 'Gesundheitswesen' },
  { key: 'energy', label: 'Energie' },
  { key: 'critical_infrastructure', label: 'Krit. Infrastruktur' },
  { key: 'digital_product_manufacturer', label: 'Digitale Produkte' },
  { key: 'manufacturing', label: 'Industrie/Fertigung' },
  { key: 'public_sector', label: 'Öffentl. Sektor' },
  { key: 'research', label: 'Forschung' },
  { key: 'other', label: 'Sonstige' },
];

const PHASES: { key: keyof PhasePT; label: string }[] = [
  { key: 'kickoff', label: 'Kickoff' },
  { key: 'docAnalysis', label: 'Dokument-Analyse' },
  { key: 'interviews', label: 'Interviews' },
  { key: 'gapAnalysis', label: 'Gap-Analyse' },
  { key: 'thirdPartyReview', label: 'Drittparteien' },
  { key: 'targetAndRoadmap', label: 'Ziel & Roadmap' },
  { key: 'reporting', label: 'Reporting' },
];

const DEFAULT_ROLES: CpcRoleDefinition[] = [
  { key: 'senior', label: 'Senior Consultant', description: 'Lead-Berater mit Framework-Expertise und Kundenkontakt' },
  { key: 'complianceManager', label: 'Compliance Manager', description: 'Fachexperte für regulatorische Anforderungen' },
  { key: 'junior', label: 'Junior Consultant', description: 'Unterstützende Analysetätigkeiten und Dokumentation' },
  { key: 'pmo', label: 'PMO', description: 'Projektkoordination, Planung und Berichtswesen' },
];

const EDITOR_SECTIONS = [
  { id: 'general', label: 'Allgemein' },
  { id: 'phasePT', label: 'Phasen-PT' },
  { id: 'roles', label: 'Rollen' },
  { id: 'roleSplit', label: 'Rollen-Split' },
  { id: 'hourlyRates', label: 'Stundensätze' },
  { id: 'factors', label: 'Faktoren' },
  { id: 'surcharges', label: 'Strukturaufschläge' },
  { id: 'maturity', label: 'Reifegrad & Add-ons' },
  { id: 'overlap', label: 'Overlap-Matrix' },
];

// ── Style-Helfer ──────────────────────────────────────────────────────────────

const thStyle: CSSProperties = {
  padding: '0.625rem 1rem', textAlign: 'left', fontSize: '0.75rem',
  textTransform: 'uppercase', color: 'var(--color-text-muted)',
  fontWeight: 600, whiteSpace: 'nowrap',
};

const tdStyle: CSSProperties = {
  padding: '0.75rem 1rem', fontSize: '0.875rem', verticalAlign: 'middle',
};

const actionBtnStyle: CSSProperties = {
  padding: '0.3rem 0.625rem', borderRadius: '0.25rem',
  border: '1px solid var(--color-border)', background: 'var(--color-bg-card)',
  fontSize: '0.75rem', cursor: 'pointer', color: 'var(--color-text)',
};

const dangerBtnStyle: CSSProperties = {
  padding: '0.3rem 0.625rem', borderRadius: '0.25rem',
  border: '1px solid #fecaca', background: '#fef2f2',
  fontSize: '0.75rem', cursor: 'pointer', color: '#dc2626',
};

const successBtnStyle: CSSProperties = {
  padding: '0.3rem 0.625rem', borderRadius: '0.25rem',
  border: '1px solid #bbf7d0', background: '#f0fdf4',
  fontSize: '0.75rem', cursor: 'pointer', color: '#166534',
};

const primaryBtnStyle: CSSProperties = {
  padding: '0.5rem 1rem', borderRadius: '0.375rem',
  border: 'none', background: 'var(--color-primary, #3B82F6)',
  color: '#fff', fontSize: '0.8125rem', fontWeight: 500, cursor: 'pointer',
};

// ── Admin-Haupt-Wrapper (Tabs: Faktor-Sets | Tagessätze) ─────────────────────

export function CpcAdminTab() {
  const [adminTab, setAdminTab] = useState<'factorSets' | 'rates' | 'clockodo' | 'auditLog'>('factorSets');

  const tabBtnStyle = (active: boolean): CSSProperties => ({
    padding: '0.5rem 1rem', borderRadius: '0.375rem 0.375rem 0 0',
    border: '1px solid var(--color-border)',
    borderBottom: active ? 'none' : '1px solid var(--color-border)',
    background: active ? 'var(--color-bg-card)' : 'transparent',
    color: active ? 'var(--color-primary, #3B82F6)' : 'var(--color-text-muted)',
    fontWeight: active ? 600 : 400,
    fontSize: '0.875rem', cursor: 'pointer',
    marginBottom: '-1px', position: 'relative' as const,
  });

  return (
    <div>
      {/* Tab-Navigation */}
      <div style={{ display: 'flex', gap: '0.25rem', borderBottom: '1px solid var(--color-border)', marginBottom: '1.5rem' }}>
        <button style={tabBtnStyle(adminTab === 'factorSets')} onClick={() => setAdminTab('factorSets')}>
          Faktor-Sets
        </button>
        <button style={tabBtnStyle(adminTab === 'rates')} onClick={() => setAdminTab('rates')}>
          Tagessätze
        </button>
        <button style={tabBtnStyle(adminTab === 'clockodo')} onClick={() => setAdminTab('clockodo')}>
          Clockodo
        </button>
        <button style={tabBtnStyle(adminTab === 'auditLog')} onClick={() => setAdminTab('auditLog')}>
          Audit-Log
        </button>
      </div>

      {adminTab === 'rates' ? (
        <RateAdminTab />
      ) : adminTab === 'clockodo' ? (
        <ClockodoAdminTab />
      ) : adminTab === 'auditLog' ? (
        <AuditLogTab />
      ) : (
        <FactorSetAdminTab />
      )}
    </div>
  );
}

// ── Faktor-Set-Verwaltung (bisheriger Inhalt von CpcAdminTab) ─────────────────

function FactorSetAdminTab() {
  const { data: factorSets = [], isLoading } = useCpcFactorSets();
  const createMutation = useCreateFactorSet();
  const activateMutation = useActivateFactorSet();
  const deleteMutation = useDeleteFactorSet();

  const [showCreate, setShowCreate] = useState(false);
  const [newVersion, setNewVersion] = useState('');
  const [newEffectiveFrom, setNewEffectiveFrom] = useState(
    new Date().toISOString().split('T')[0],
  );
  const [newNotes, setNewNotes] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const handleCreate = () => {
    if (!newVersion.trim()) return;
    createMutation.mutate(
      { version: newVersion.trim(), effectiveFrom: newEffectiveFrom, notes: newNotes || undefined },
      {
        onSuccess: () => {
          setShowCreate(false);
          setNewVersion('');
          setNewNotes('');
        },
      },
    );
  };

  if (isLoading) {
    return <p style={{ padding: '2rem', color: 'var(--color-text-muted)' }}>Laden...</p>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h3 style={{ fontSize: '1rem', fontWeight: 600, margin: 0 }}>CPC Faktor-Sets</h3>
          <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)', margin: '0.25rem 0 0' }}>
            Verwalten Sie Kalkulations-Parameter mit Versionierung. Entwürfe können bearbeitet und dann aktiviert werden.
          </p>
        </div>
        <button type="button" onClick={() => setShowCreate(true)} style={primaryBtnStyle}>
          + Neue Version
        </button>
      </div>

      {/* Neue Version erstellen */}
      {showCreate && (
        <div style={{
          background: 'var(--color-bg-card)', borderRadius: 'var(--radius-md)',
          border: '1px solid var(--color-primary, #3B82F6)',
          boxShadow: 'var(--shadow-sm)', padding: '1.25rem',
        }}>
          <h4 style={{ fontSize: '0.875rem', fontWeight: 600, margin: '0 0 1rem' }}>
            Neue Version erstellen
            <span style={{ fontWeight: 400, color: 'var(--color-text-muted)', marginLeft: '0.5rem' }}>
              (Klon des aktiven Faktor-Sets)
            </span>
          </h4>
          <div style={{ display: 'grid', gridTemplateColumns: '160px 180px 1fr', gap: '0.75rem' }}>
            <div>
              <label style={labelStyle}>Version *</label>
              <input
                type="text"
                value={newVersion}
                onChange={(e) => setNewVersion(e.target.value)}
                placeholder="z.B. 1.2.0"
                style={inputStyle}
                autoFocus
              />
            </div>
            <div>
              <label style={labelStyle}>Gültig ab</label>
              <input
                type="date"
                value={newEffectiveFrom}
                onChange={(e) => setNewEffectiveFrom(e.target.value)}
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Notizen</label>
              <input
                type="text"
                value={newNotes}
                onChange={(e) => setNewNotes(e.target.value)}
                placeholder="Optionale Beschreibung"
                style={inputStyle}
              />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
            <button
              type="button"
              onClick={() => { setShowCreate(false); setNewVersion(''); setNewNotes(''); }}
              style={actionBtnStyle}
            >
              Abbrechen
            </button>
            <button
              type="button"
              onClick={handleCreate}
              disabled={createMutation.isPending || !newVersion.trim()}
              style={{ ...primaryBtnStyle, opacity: createMutation.isPending || !newVersion.trim() ? 0.6 : 1 }}
            >
              {createMutation.isPending ? 'Erstellen...' : 'Erstellen'}
            </button>
          </div>
        </div>
      )}

      {/* Versions-Tabelle */}
      <div style={{
        background: 'var(--color-bg-card)', borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--shadow-sm)', border: '1px solid var(--color-border)', overflow: 'hidden',
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--color-border)', background: 'var(--color-bg)' }}>
              <th style={thStyle}>Version</th>
              <th style={thStyle}>Gültig ab</th>
              <th style={thStyle}>Status</th>
              <th style={thStyle}>Erstellt am</th>
              <th style={thStyle}>Notizen</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {factorSets.length === 0 && (
              <tr>
                <td colSpan={6} style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>
                  Keine Faktor-Sets vorhanden.
                </td>
              </tr>
            )}
            {factorSets.map((fs) => (
              <tr
                key={fs.id}
                style={{
                  borderBottom: '1px solid var(--color-border)',
                  background: editingId === fs.id ? 'var(--color-bg)' : undefined,
                }}
              >
                <td style={tdStyle}>
                  <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{fs.version}</span>
                </td>
                <td style={tdStyle}>
                  {new Date(fs.effectiveFrom).toLocaleDateString('de-DE')}
                </td>
                <td style={tdStyle}>
                  <span style={{
                    padding: '0.125rem 0.5rem', borderRadius: '9999px',
                    fontSize: '0.75rem', fontWeight: 500,
                    background: fs.isActive ? '#dcfce7' : '#f3f4f6',
                    color: fs.isActive ? '#166534' : '#6b7280',
                    border: `1px solid ${fs.isActive ? '#bbf7d0' : '#e5e7eb'}`,
                  }}>
                    {fs.isActive ? 'Aktiv' : 'Entwurf'}
                  </span>
                </td>
                <td style={{ ...tdStyle, color: 'var(--color-text-muted)' }}>
                  {new Date(fs.createdAt).toLocaleDateString('de-DE')}
                </td>
                <td style={{
                  ...tdStyle, maxWidth: 220,
                  color: 'var(--color-text-muted)', fontSize: '0.8125rem',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {fs.notes ?? '—'}
                </td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>
                  <div style={{ display: 'flex', gap: '0.375rem', justifyContent: 'flex-end', alignItems: 'center' }}>
                    <button
                      type="button"
                      onClick={() => setEditingId(editingId === fs.id ? null : fs.id)}
                      style={editingId === fs.id
                        ? { ...actionBtnStyle, background: 'var(--color-bg)', fontWeight: 600 }
                        : actionBtnStyle
                      }
                    >
                      {editingId === fs.id ? 'Schließen' : 'Bearbeiten'}
                    </button>
                    {!fs.isActive && (
                      <>
                        <button
                          type="button"
                          onClick={() => activateMutation.mutate(fs.id)}
                          disabled={activateMutation.isPending}
                          style={successBtnStyle}
                        >
                          Aktivieren
                        </button>
                        {deleteConfirmId === fs.id ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.75rem' }}>
                            <span style={{ color: '#dc2626' }}>Löschen?</span>
                            <button
                              type="button"
                              onClick={() => deleteMutation.mutate(fs.id, { onSuccess: () => setDeleteConfirmId(null) })}
                              style={dangerBtnStyle}
                            >
                              Ja
                            </button>
                            <button type="button" onClick={() => setDeleteConfirmId(null)} style={actionBtnStyle}>
                              Nein
                            </button>
                          </span>
                        ) : (
                          <button type="button" onClick={() => setDeleteConfirmId(fs.id)} style={dangerBtnStyle}>
                            Löschen
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Inline-Editor */}
      {editingId && (
        <VersionEditor id={editingId} onClose={() => setEditingId(null)} />
      )}
    </div>
  );
}

// ── Payload-Editor ────────────────────────────────────────────────────────────

type UpdateFn = (mutate: (d: FactorSet) => void) => void;

function VersionEditor({ id, onClose }: { id: string; onClose: () => void }) {
  const { data: detail, isLoading } = useCpcFactorSetDetail(id);
  const updateMutation = useUpdateFactorSet();

  const [payload, setPayload] = useState<FactorSet | null>(null);
  const [section, setSection] = useState('general');
  const [savedOk, setSavedOk] = useState(false);

  useEffect(() => {
    if (detail?.payload) {
      const pl = JSON.parse(JSON.stringify(detail.payload)) as FactorSet;
      if (!pl.roles || pl.roles.length === 0) {
        pl.roles = DEFAULT_ROLES;
      }
      setPayload(pl);
    }
  }, [detail]);

  const update: UpdateFn = (fn) => {
    setPayload((prev) => {
      if (!prev) return prev;
      const next = JSON.parse(JSON.stringify(prev)) as FactorSet;
      fn(next);
      return next;
    });
  };

  const handleSave = () => {
    if (!payload) return;
    updateMutation.mutate(
      { id, data: { payload } },
      {
        onSuccess: () => {
          setSavedOk(true);
          setTimeout(() => setSavedOk(false), 2500);
        },
      },
    );
  };

  if (isLoading || !payload) {
    return (
      <div style={{
        background: 'var(--color-bg-card)', borderRadius: 'var(--radius-md)',
        border: '1px solid var(--color-border)', padding: '2rem',
        textAlign: 'center', color: 'var(--color-text-muted)',
      }}>
        Parameter werden geladen...
      </div>
    );
  }

  return (
    <div style={{
      background: 'var(--color-bg-card)', borderRadius: 'var(--radius-md)',
      border: '1px solid var(--color-border)', boxShadow: 'var(--shadow-sm)', overflow: 'hidden',
    }}>
      {/* Editor-Header */}
      <div style={{
        padding: '0.875rem 1.25rem', borderBottom: '1px solid var(--color-border)',
        background: 'var(--color-bg)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <div>
          <span style={{ fontSize: '0.875rem', fontWeight: 600 }}>
            Parameter bearbeiten — Version {detail?.version}
          </span>
          <span style={{
            marginLeft: '0.5rem', fontSize: '0.75rem',
            color: detail?.isActive ? '#166534' : 'var(--color-text-muted)',
            fontWeight: detail?.isActive ? 600 : 400,
          }}>
            {detail?.isActive ? '● Aktiv' : 'Entwurf'}
          </span>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          {savedOk && (
            <span style={{ fontSize: '0.75rem', color: '#166534', fontWeight: 500 }}>
              ✓ Gespeichert
            </span>
          )}
          {updateMutation.isError && (
            <span style={{ fontSize: '0.75rem', color: '#dc2626' }}>Fehler beim Speichern</span>
          )}
          <button
            type="button"
            onClick={handleSave}
            disabled={updateMutation.isPending}
            style={{ ...primaryBtnStyle, padding: '0.375rem 0.875rem', opacity: updateMutation.isPending ? 0.6 : 1 }}
          >
            {updateMutation.isPending ? 'Speichern...' : 'Speichern'}
          </button>
          <button type="button" onClick={onClose} style={actionBtnStyle}>
            Schließen
          </button>
        </div>
      </div>

      {/* Warnung bei aktivem Set */}
      {detail?.isActive && (
        <div style={{
          padding: '0.625rem 1.25rem',
          background: '#fffbeb', borderBottom: '1px solid #fde68a',
          fontSize: '0.8125rem', color: '#92400e',
        }}>
          Achtung: Sie bearbeiten das aktive Faktor-Set. Gespeicherte Änderungen wirken sich sofort auf neue Kalkulationen aus.
        </div>
      )}

      {/* Section-Tabs */}
      <div style={{
        display: 'flex', borderBottom: '1px solid var(--color-border)',
        background: 'var(--color-bg)', overflowX: 'auto',
      }}>
        {EDITOR_SECTIONS.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => setSection(s.id)}
            style={{
              padding: '0.625rem 1.125rem', border: 'none', borderRadius: 0,
              background: 'transparent',
              borderBottom: section === s.id
                ? '2px solid var(--color-primary, #3B82F6)'
                : '2px solid transparent',
              fontSize: '0.8125rem',
              fontWeight: section === s.id ? 600 : 400,
              color: section === s.id ? 'var(--color-primary, #3B82F6)' : 'var(--color-text)',
              cursor: 'pointer', whiteSpace: 'nowrap',
            }}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* Section-Inhalt */}
      <div style={{ padding: '1.25rem' }}>
        {section === 'general' && <GeneralSection payload={payload} update={update} />}
        {section === 'phasePT' && <PhasePtSection payload={payload} update={update} />}
        {section === 'roles' && <RolesSection payload={payload} update={update} />}
        {section === 'roleSplit' && <RoleSplitSection payload={payload} update={update} />}
        {section === 'hourlyRates' && <HourlyRatesSection payload={payload} update={update} />}
        {section === 'factors' && <FactorsSection payload={payload} update={update} />}
        {section === 'surcharges' && <SurchargesSection payload={payload} update={update} />}
        {section === 'maturity' && <MaturityAddOnSection payload={payload} update={update} />}
        {section === 'overlap' && <OverlapMatrixSection payload={payload} update={update} />}
      </div>
    </div>
  );
}

// ── Shared Primitives ─────────────────────────────────────────────────────────

const labelStyle: CSSProperties = {
  display: 'block', fontSize: '0.75rem', fontWeight: 600,
  color: 'var(--color-text-muted)', marginBottom: '0.25rem',
};

const inputStyle: CSSProperties = {
  width: '100%', padding: '0.5rem 0.75rem', borderRadius: '0.375rem',
  border: '1px solid var(--color-border)', fontSize: '0.875rem',
  background: 'var(--color-bg)', color: 'var(--color-text)', boxSizing: 'border-box',
};

function NumIn({
  value,
  onChange,
  step = 0.01,
  min,
  max,
  width = 80,
}: {
  value: number;
  onChange: (v: number) => void;
  step?: number;
  min?: number;
  max?: number;
  width?: number;
}) {
  return (
    <input
      type="number"
      value={value}
      onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
      step={step}
      min={min}
      max={max}
      style={{
        width, padding: '0.25rem 0.375rem',
        border: '1px solid var(--color-border)', borderRadius: '0.25rem',
        fontSize: '0.8125rem', background: 'var(--color-bg)', color: 'var(--color-text)',
      }}
    />
  );
}

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <div style={{
      fontSize: '0.6875rem', textTransform: 'uppercase', fontWeight: 600,
      color: 'var(--color-text-muted)', marginBottom: '0.75rem', marginTop: '1.25rem',
      borderBottom: '1px solid var(--color-border)', paddingBottom: '0.375rem',
    }}>
      {children}
    </div>
  );
}

function FieldRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.375rem 0' }}>
      <span style={{ fontSize: '0.875rem', color: 'var(--color-text)' }}>{label}</span>
      {children}
    </div>
  );
}

// ── Sections ──────────────────────────────────────────────────────────────────

function GeneralSection({ payload, update }: { payload: FactorSet; update: UpdateFn }) {
  return (
    <div style={{ maxWidth: 480 }}>
      <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)', marginTop: 0, marginBottom: '1rem' }}>
        Globale Multiplikatoren, die auf alle Kalkulationen angewendet werden. Der BAIT-Modifier erhöht den Aufwand bei aktivierter BAIT-Prüfung.
        Die Bandbreiten-Multiplikatoren definieren den minimalen und maximalen PT-Korridor um den Erwartungswert.
      </p>
      <SectionLabel>Allgemeine Multiplikatoren</SectionLabel>
      <FieldRow label="BAIT-Modifier">
        <NumIn value={payload.baitModifier} onChange={(v) => update((d) => { d.baitModifier = v; })} step={0.05} min={0} />
      </FieldRow>
      <FieldRow label="Annahme-Risiko-Multiplikator">
        <NumIn value={payload.assumptionRiskMultiplier} onChange={(v) => update((d) => { d.assumptionRiskMultiplier = v; })} step={0.05} min={1} />
      </FieldRow>

      <SectionLabel>Bandbreiten</SectionLabel>
      <FieldRow label="Min-Multiplikator">
        <NumIn value={payload.rangeMultipliers.min} onChange={(v) => update((d) => { d.rangeMultipliers.min = v; })} step={0.05} min={0} max={1} />
      </FieldRow>
      <FieldRow label="Max-Multiplikator">
        <NumIn value={payload.rangeMultipliers.max} onChange={(v) => update((d) => { d.rangeMultipliers.max = v; })} step={0.05} min={1} />
      </FieldRow>
    </div>
  );
}

function PhasePtSection({ payload, update }: { payload: FactorSet; update: UpdateFn }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)', marginTop: 0, marginBottom: '1rem' }}>
        PT-Werte pro Phase und Unternehmensklasse (S/M/L). Werden mit Framework-Faktoren multipliziert.
      </p>
      <table style={{ borderCollapse: 'collapse', width: '100%' }}>
        <thead>
          <tr style={{ background: 'var(--color-bg)' }}>
            <th style={{ ...thStyle, width: 160 }}>Phase</th>
            {SIZE_CLASSES.map((sc) => (
              <th key={sc} style={{ ...thStyle, textAlign: 'center', width: 100 }}>Klasse {sc}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {PHASES.map((phase) => (
            <tr key={phase.key} style={{ borderBottom: '1px solid var(--color-border)' }}>
              <td style={tdStyle}>{phase.label}</td>
              {SIZE_CLASSES.map((sc) => (
                <td key={sc} style={{ ...tdStyle, textAlign: 'center' }}>
                  <NumIn
                    value={payload.phasePT[sc]?.[phase.key] ?? 0}
                    onChange={(v) => update((d) => { d.phasePT[sc][phase.key] = v; })}
                    step={0.5}
                    min={0}
                    width={72}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RoleSplitSection({ payload, update }: { payload: FactorSet; update: UpdateFn }) {
  const roles = payload.roles ?? DEFAULT_ROLES;
  return (
    <div style={{ overflowX: 'auto' }}>
      <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)', marginTop: 0, marginBottom: '1rem' }}>
        Prozentualer Anteil jeder Rolle am Gesamtaufwand, getrennt nach Unternehmensklasse (S/M/L).
        Die Summe pro Klasse muss 1,0 (= 100 %) ergeben, damit die Kalkulation korrekt ist.
        Rollen können im Tab „Rollen" verwaltet werden.
      </p>
      <table style={{ borderCollapse: 'collapse', width: '100%' }}>
        <thead>
          <tr style={{ background: 'var(--color-bg)' }}>
            <th style={{ ...thStyle, width: 180 }}>Rolle</th>
            {SIZE_CLASSES.map((sc) => (
              <th key={sc} style={{ ...thStyle, textAlign: 'center', width: 100 }}>Klasse {sc}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {roles.map((role) => (
            <tr key={role.key} style={{ borderBottom: '1px solid var(--color-border)' }}>
              <td style={tdStyle}>{role.label}</td>
              {SIZE_CLASSES.map((sc) => (
                <td key={sc} style={{ ...tdStyle, textAlign: 'center' }}>
                  <NumIn
                    value={payload.roleSplit[sc]?.[role.key] ?? 0}
                    onChange={(v) => update((d) => { d.roleSplit[sc][role.key] = v; })}
                    step={0.01}
                    min={0}
                    max={1}
                    width={72}
                  />
                </td>
              ))}
            </tr>
          ))}
          {/* Summen-Zeile */}
          <tr style={{ background: 'var(--color-bg)', fontWeight: 600 }}>
            <td style={tdStyle}>Summe</td>
            {SIZE_CLASSES.map((sc) => {
              const sum = roles.reduce((a, r) => a + (payload.roleSplit[sc]?.[r.key] ?? 0), 0);
              const ok = Math.abs(sum - 1) < 0.001;
              return (
                <td key={sc} style={{ ...tdStyle, textAlign: 'center', color: ok ? '#166534' : '#dc2626' }}>
                  {sum.toFixed(2)}
                </td>
              );
            })}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function HourlyRatesSection({ payload, update }: { payload: FactorSet; update: UpdateFn }) {
  const roles = payload.roles ?? DEFAULT_ROLES;
  const currentRates = payload.hourlyRates ?? {};

  return (
    <div style={{ maxWidth: 620 }}>
      <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)', marginTop: 0, marginBottom: '1rem' }}>
        Stundensätze in EUR netto pro Rolle. Werden für die Umrechnung von Personentagen (PT) in Honorarvolumen verwendet.
        Ein Personentag entspricht 8 Arbeitsstunden. Rollen können im Tab „Rollen" verwaltet werden.
      </p>
      <table style={{ borderCollapse: 'collapse', width: '100%' }}>
        <thead>
          <tr style={{ background: 'var(--color-bg)' }}>
            <th style={{ ...thStyle, width: 200 }}>Rolle</th>
            <th style={{ ...thStyle }}>Beschreibung</th>
            <th style={{ ...thStyle, textAlign: 'right', width: 140 }}>Stundensatz (€/h)</th>
            <th style={{ ...thStyle, textAlign: 'right', width: 140 }}>Tagessatz (8h)</th>
          </tr>
        </thead>
        <tbody>
          {roles.map((role) => {
            const hourly = currentRates[role.key] ?? 0;
            return (
              <tr key={role.key} style={{ borderBottom: '1px solid var(--color-border)' }}>
                <td style={{ ...tdStyle, fontWeight: 500 }}>{role.label}</td>
                <td style={{ ...tdStyle, fontSize: '0.8125rem', color: 'var(--color-text-muted)' }}>
                  {role.description ?? '—'}
                </td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.375rem' }}>
                    <NumIn
                      value={hourly}
                      onChange={(v) => update((d) => {
                        if (!d.hourlyRates) d.hourlyRates = {};
                        d.hourlyRates[role.key] = v;
                      })}
                      step={5}
                      min={0}
                      width={90}
                    />
                    <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)' }}>€/h</span>
                  </div>
                </td>
                <td style={{ ...tdStyle, textAlign: 'right', fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
                  {(hourly * 8).toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} €
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function makeRoleKey(label: string): string {
  const words = label.trim().split(/\s+/);
  return words
    .map((w, i) => i === 0 ? w.toLowerCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join('')
    .replace(/[^a-zA-Z0-9]/g, '') || `role_${Date.now()}`;
}

function RolesSection({ payload, update }: { payload: FactorSet; update: UpdateFn }) {
  const roles = payload.roles ?? DEFAULT_ROLES;
  const [newLabel, setNewLabel] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [showAdd, setShowAdd] = useState(false);

  const addRole = () => {
    if (!newLabel.trim()) return;
    const key = makeRoleKey(newLabel);
    if (roles.some((r) => r.key === key)) return;
    update((d) => {
      d.roles.push({ key, label: newLabel.trim(), description: newDesc.trim() || undefined });
      SIZE_CLASSES.forEach((sc) => {
        if (!d.roleSplit[sc]) d.roleSplit[sc] = {};
        d.roleSplit[sc][key] = 0;
      });
      if (!d.hourlyRates) d.hourlyRates = {};
      d.hourlyRates[key] = 0;
    });
    setNewLabel('');
    setNewDesc('');
    setShowAdd(false);
  };

  const removeRole = (key: string) => {
    update((d) => {
      d.roles = d.roles.filter((r) => r.key !== key);
      SIZE_CLASSES.forEach((sc) => {
        if (d.roleSplit[sc]) delete d.roleSplit[sc][key];
      });
      if (d.hourlyRates) delete d.hourlyRates[key];
    });
  };

  const updateRole = (key: string, field: 'label' | 'description', value: string) => {
    update((d) => {
      const role = d.roles.find((r) => r.key === key);
      if (role) role[field] = value;
    });
  };

  return (
    <div style={{ maxWidth: 680 }}>
      <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)', marginTop: 0, marginBottom: '1rem' }}>
        Definieren Sie die Projektrollen, die in Kalkulationen verwendet werden. Jede Rolle erhält im
        Tab „Rollen-Split" eine prozentuale Gewichtung und im Tab „Stundensätze" einen Stundensatz.
        Das interne Kürzel (Key) wird beim Erstellen automatisch vergeben und danach nicht mehr geändert.
      </p>
      <table style={{ borderCollapse: 'collapse', width: '100%', marginBottom: '0.75rem' }}>
        <thead>
          <tr style={{ background: 'var(--color-bg)' }}>
            <th style={{ ...thStyle, width: 120 }}>Kürzel</th>
            <th style={{ ...thStyle, width: 200 }}>Bezeichnung</th>
            <th style={{ ...thStyle }}>Beschreibung</th>
            <th style={{ ...thStyle, width: 48 }} />
          </tr>
        </thead>
        <tbody>
          {roles.map((role) => (
            <tr key={role.key} style={{ borderBottom: '1px solid var(--color-border)' }}>
              <td style={tdStyle}>
                <span style={{
                  fontFamily: 'monospace', fontSize: '0.75rem',
                  background: 'var(--color-bg)', border: '1px solid var(--color-border)',
                  borderRadius: '0.25rem', padding: '0.125rem 0.375rem',
                  color: 'var(--color-text-muted)',
                }}>
                  {role.key}
                </span>
              </td>
              <td style={tdStyle}>
                <input
                  type="text"
                  value={role.label}
                  onChange={(e) => updateRole(role.key, 'label', e.target.value)}
                  style={{ ...inputStyle, width: '100%' }}
                />
              </td>
              <td style={tdStyle}>
                <input
                  type="text"
                  value={role.description ?? ''}
                  onChange={(e) => updateRole(role.key, 'description', e.target.value)}
                  placeholder="Optionale Beschreibung"
                  style={{ ...inputStyle, width: '100%' }}
                />
              </td>
              <td style={{ ...tdStyle, textAlign: 'center' }}>
                <button
                  type="button"
                  onClick={() => removeRole(role.key)}
                  disabled={roles.length <= 1}
                  style={{
                    ...dangerBtnStyle,
                    padding: '0.125rem 0.375rem',
                    opacity: roles.length <= 1 ? 0.4 : 1,
                    cursor: roles.length <= 1 ? 'not-allowed' : 'pointer',
                  }}
                  title={roles.length <= 1 ? 'Mindestens eine Rolle erforderlich' : 'Rolle entfernen'}
                >
                  ✕
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {showAdd ? (
        <div style={{
          background: 'var(--color-bg)', border: '1px solid var(--color-primary, #3B82F6)',
          borderRadius: '0.375rem', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem',
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '0.75rem' }}>
            <div>
              <label style={labelStyle}>Bezeichnung *</label>
              <input
                type="text"
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                placeholder="z.B. Senior Manager"
                style={inputStyle}
                autoFocus
                onKeyDown={(e) => { if (e.key === 'Enter') addRole(); }}
              />
            </div>
            <div>
              <label style={labelStyle}>Beschreibung</label>
              <input
                type="text"
                value={newDesc}
                onChange={(e) => setNewDesc(e.target.value)}
                placeholder="Optionale Beschreibung der Rolle"
                style={inputStyle}
              />
            </div>
          </div>
          {newLabel.trim() && (
            <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
              Kürzel: <code style={{ fontFamily: 'monospace' }}>{makeRoleKey(newLabel)}</code>
            </div>
          )}
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
            <button type="button" onClick={() => { setShowAdd(false); setNewLabel(''); setNewDesc(''); }} style={actionBtnStyle}>
              Abbrechen
            </button>
            <button
              type="button"
              onClick={addRole}
              disabled={!newLabel.trim() || roles.some((r) => r.key === makeRoleKey(newLabel))}
              style={{
                ...primaryBtnStyle,
                opacity: !newLabel.trim() || roles.some((r) => r.key === makeRoleKey(newLabel)) ? 0.6 : 1,
              }}
            >
              Hinzufügen
            </button>
          </div>
        </div>
      ) : (
        <button type="button" onClick={() => setShowAdd(true)} style={actionBtnStyle}>
          + Neue Rolle
        </button>
      )}
    </div>
  );
}

function FactorsSection({ payload, update }: { payload: FactorSet; update: UpdateFn }) {
  return (
    <div>
      <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)', marginTop: 0, marginBottom: '1.25rem' }}>
        Multiplikatoren für branchen- und situationsspezifische Einflüsse. Ein Faktor von 1,0 bedeutet keine Anpassung.
        Werte über 1,0 erhöhen den Aufwand (z.B. stark regulierter Sektor), Werte unter 1,0 reduzieren ihn.
      </p>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
      {/* Sektor-Faktoren */}
      <div>
        <SectionLabel>Sektor-Faktoren</SectionLabel>
        {SECTORS.map(({ key, label }) => (
          <FieldRow key={key} label={label}>
            <NumIn
              value={payload.sectorFactor?.[key] ?? 1}
              onChange={(v) => update((d) => { d.sectorFactor[key] = v; })}
              step={0.05}
              min={0}
            />
          </FieldRow>
        ))}
      </div>

      <div>
        {/* Standort-Faktoren */}
        <SectionLabel>Standort-Faktoren</SectionLabel>
        {([['de_only', 'Deutschland'], ['eu', 'EU'], ['global', 'Global']] as const).map(([key, label]) => (
          <FieldRow key={key} label={label}>
            <NumIn
              value={payload.locationFactor?.[key] ?? 1}
              onChange={(v) => update((d) => { d.locationFactor[key] = v; })}
              step={0.05}
              min={0}
            />
          </FieldRow>
        ))}

        {/* DSGVO-Faktoren */}
        <SectionLabel>DSGVO-Faktoren</SectionLabel>
        {([['none', 'Kein DSGVO-Scope'], ['standard', 'Standard'], ['deepDive', 'Deep Dive']] as const).map(([key, label]) => (
          <FieldRow key={key} label={label}>
            <NumIn
              value={payload.dsgvoFactor?.[key] ?? 1}
              onChange={(v) => update((d) => { d.dsgvoFactor[key] = v; })}
              step={0.05}
              min={0}
            />
          </FieldRow>
        ))}

        {/* Dokumentations-Faktoren */}
        <SectionLabel>Dokumentations-Faktoren</SectionLabel>
        {([['complete', 'Vollständig'], ['partial', 'Teilweise'], ['missing', 'Fehlend']] as const).map(([key, label]) => (
          <FieldRow key={key} label={label}>
            <NumIn
              value={payload.docFactor?.[key] ?? 1}
              onChange={(v) => update((d) => { d.docFactor[key] = v; })}
              step={0.05}
              min={0}
            />
          </FieldRow>
        ))}

        {/* Tiefe-Faktoren */}
        <SectionLabel>Prüfungstiefe-Faktoren</SectionLabel>
        {([['standardCheck', 'Standard Check'], ['premiumAssessment', 'Premium Assessment']] as const).map(([key, label]) => (
          <FieldRow key={key} label={label}>
            <NumIn
              value={payload.depthFactor?.[key] ?? 1}
              onChange={(v) => update((d) => { d.depthFactor[key] = v; })}
              step={0.05}
              min={0}
            />
          </FieldRow>
        ))}
      </div>
    </div>
    </div>
  );
}

function SurchargesSection({ payload, update }: { payload: FactorSet; update: UpdateFn }) {
  const rules = payload.thirdPartySurcharge ?? [];

  const addRule = () =>
    update((d) => {
      d.thirdPartySurcharge.push({ maxCount: null, surcharge: 0 });
    });

  const removeRule = (i: number) =>
    update((d) => {
      d.thirdPartySurcharge.splice(i, 1);
    });

  return (
    <div>
      <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)', marginTop: 0, marginBottom: '1.25rem' }}>
        Strukturelle Aufschläge für besondere Prüfungsanforderungen. Die Baseline definiert den Grundaufwand für kritische Funktionen;
        jede weitere kritische Funktion über die Baseline hinaus erzeugt einen zusätzlichen PT-Aufschlag.
        Drittanbieter-Aufschlagsregeln staffeln den Aufwand nach Anzahl eingebundener Drittparteien.
      </p>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
      {/* Kritische Funktionen */}
      <div>
        <SectionLabel>Kritische Funktionen Baseline (PT)</SectionLabel>
        {SIZE_CLASSES.map((sc) => (
          <FieldRow key={sc} label={`Klasse ${sc}`}>
            <NumIn
              value={payload.criticalFunctionsBaseline?.[sc] ?? 0}
              onChange={(v) => update((d) => { d.criticalFunctionsBaseline[sc] = v; })}
              step={0.5}
              min={0}
            />
          </FieldRow>
        ))}
        <SectionLabel>Aufschlag pro weitere kritische Funktion (PT)</SectionLabel>
        <FieldRow label="PT pro Extra-Funktion">
          <NumIn
            value={payload.criticalFunctionsSurchargePerExtra ?? 0}
            onChange={(v) => update((d) => { d.criticalFunctionsSurchargePerExtra = v; })}
            step={0.5}
            min={0}
          />
        </FieldRow>
      </div>

      {/* Drittanbieter-Aufschlag */}
      <div>
        <SectionLabel>Drittanbieter-Aufschlagsregeln</SectionLabel>
        <table style={{ borderCollapse: 'collapse', width: '100%', marginBottom: '0.75rem' }}>
          <thead>
            <tr style={{ background: 'var(--color-bg)' }}>
              <th style={{ ...thStyle, width: 120 }}>Max. Anzahl</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>Aufschlag (PT)</th>
              <th style={{ ...thStyle, width: 40 }} />
            </tr>
          </thead>
          <tbody>
            {rules.map((rule, i) => (
              <tr key={i} style={{ borderBottom: '1px solid var(--color-border)' }}>
                <td style={tdStyle}>
                  <input
                    type="number"
                    value={rule.maxCount ?? ''}
                    placeholder="∞"
                    onChange={(e) => update((d) => {
                      d.thirdPartySurcharge[i].maxCount = e.target.value === '' ? null : parseInt(e.target.value, 10);
                    })}
                    style={{ width: 80, padding: '0.25rem 0.375rem', border: '1px solid var(--color-border)', borderRadius: '0.25rem', fontSize: '0.8125rem', background: 'var(--color-bg)', color: 'var(--color-text)' }}
                  />
                </td>
                <td style={{ ...tdStyle, textAlign: 'center' }}>
                  <NumIn
                    value={rule.surcharge}
                    onChange={(v) => update((d) => { d.thirdPartySurcharge[i].surcharge = v; })}
                    step={0.5}
                    min={0}
                    width={80}
                  />
                </td>
                <td style={{ ...tdStyle, textAlign: 'center' }}>
                  <button type="button" onClick={() => removeRule(i)} style={{ ...dangerBtnStyle, padding: '0.125rem 0.375rem' }}>
                    ✕
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <button type="button" onClick={addRule} style={actionBtnStyle}>
          + Regel hinzufügen
        </button>
      </div>
    </div>
    </div>
  );
}

function MaturityAddOnSection({ payload, update }: { payload: FactorSet; update: UpdateFn }) {
  return (
    <div>
      <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)', marginTop: 0, marginBottom: '1.25rem' }}>
        Reifegrad-Rabatte reduzieren den Aufwand bei nachgewiesener Zertifizierung (Wert = prozentualer Abzug, z.B. 0,10 = 10 %).
        Der Max-Rabatt begrenzt die Summe aller Rabatte. Add-ons sind optionale Zusatzleistungen mit eigenem PT-Aufwand.
      </p>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
      {/* Reifegrad-Rabatt */}
      <div>
        <SectionLabel>Reifegrad-Rabatt</SectionLabel>
        <FieldRow label="ISMS dokumentiert">
          <NumIn
            value={payload.maturityDiscount?.ismsDocumented ?? 0}
            onChange={(v) => update((d) => { d.maturityDiscount.ismsDocumented = v; })}
            step={0.05}
            min={0}
            max={1}
          />
        </FieldRow>
        <FieldRow label="ISMS zertifiziert">
          <NumIn
            value={payload.maturityDiscount?.ismsCertified ?? 0}
            onChange={(v) => update((d) => { d.maturityDiscount.ismsCertified = v; })}
            step={0.05}
            min={0}
            max={1}
          />
        </FieldRow>
        <FieldRow label="BCMS zertifiziert (Bonus)">
          <NumIn
            value={payload.maturityDiscount?.bcmsCertifiedBonus ?? 0}
            onChange={(v) => update((d) => { d.maturityDiscount.bcmsCertifiedBonus = v; })}
            step={0.05}
            min={0}
            max={1}
          />
        </FieldRow>
        <FieldRow label="Max. Rabatt gesamt">
          <NumIn
            value={payload.maturityDiscount?.maxTotal ?? 0}
            onChange={(v) => update((d) => { d.maturityDiscount.maxTotal = v; })}
            step={0.05}
            min={0}
            max={1}
          />
        </FieldRow>
      </div>

      {/* Add-ons */}
      <div>
        <SectionLabel>Add-ons (PT)</SectionLabel>
        <FieldRow label="Executive Training">
          <NumIn
            value={payload.addOns?.executiveTraining ?? 0}
            onChange={(v) => update((d) => { d.addOns.executiveTraining = v; })}
            step={0.5}
            min={0}
          />
        </FieldRow>
        <SectionLabel>Audit-Readiness-Check (PT nach Klasse)</SectionLabel>
        {SIZE_CLASSES.map((sc) => (
          <FieldRow key={sc} label={`Klasse ${sc}`}>
            <NumIn
              value={payload.addOns?.auditReadinessCheck?.[sc] ?? 0}
              onChange={(v) => update((d) => { d.addOns.auditReadinessCheck[sc] = v; })}
              step={0.5}
              min={0}
            />
          </FieldRow>
        ))}
      </div>
    </div>
    </div>
  );
}

function OverlapMatrixSection({ payload, update }: { payload: FactorSet; update: UpdateFn }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)', marginTop: 0, marginBottom: '1rem' }}>
        Overlap-Koeffizient zwischen Framework-Paaren (0 = kein Overlap, 1 = vollständiger Overlap).
        Diagonal-Zellen werden ignoriert.
      </p>
      <table style={{ borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: 'var(--color-bg)' }}>
            <th style={{ ...thStyle, width: 130 }}>Framework</th>
            {FRAMEWORKS.map((fw) => (
              <th key={fw} style={{ ...thStyle, textAlign: 'center', width: 84, fontSize: '0.6875rem' }}>
                {FRAMEWORK_LABELS[fw]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {FRAMEWORKS.map((rowFw) => (
            <tr key={rowFw} style={{ borderBottom: '1px solid var(--color-border)' }}>
              <td style={{ ...tdStyle, fontWeight: 500, fontSize: '0.8125rem', whiteSpace: 'nowrap' }}>
                {FRAMEWORK_LABELS[rowFw]}
              </td>
              {FRAMEWORKS.map((colFw) => {
                const isDiag = rowFw === colFw;
                const val = payload.overlapMatrix?.[rowFw]?.[colFw];
                return (
                  <td key={colFw} style={{ ...tdStyle, textAlign: 'center', padding: '0.375rem' }}>
                    {isDiag ? (
                      <span style={{ color: 'var(--color-text-muted)' }}>—</span>
                    ) : (
                      <NumIn
                        value={val ?? 0}
                        onChange={(v) => update((d) => {
                          if (!d.overlapMatrix[rowFw]) d.overlapMatrix[rowFw] = {} as any;
                          d.overlapMatrix[rowFw][colFw] = v;
                        })}
                        step={0.05}
                        min={0}
                        max={1}
                        width={64}
                      />
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
